import { SESSION_STATUS_LABELS, type SessionStatus, type SessionSummary } from '@diana/contracts';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { Link } from 'react-router-dom';
import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AdminApiError } from '../../lib/api-client.js';
import { shootClientSummary } from './shoot-list.js';

export type MoveShoot = (shoot: SessionSummary, status: SessionStatus) => Promise<void | boolean>;
const DragState = createContext({ disabled: true });
const columnKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  { context, currentCoordinates },
) => {
  if (event.code !== 'ArrowRight' && event.code !== 'ArrowLeft') return;
  event.preventDefault();
  const columns = context.droppableContainers
    .getEnabled()
    .filter((item) => item.data.current?.status)
    .sort(
      (a, b) =>
        (context.droppableRects.get(a.id)?.left ?? 0) -
        (context.droppableRects.get(b.id)?.left ?? 0),
    );
  const source = context.over?.id ?? `status:${context.active?.data.current?.shoot?.status}`;
  const index = columns.findIndex((item) => item.id === source);
  const next = columns[index + (event.code === 'ArrowRight' ? 1 : -1)];
  const current = columns[index];
  if (!current || !next) return;
  const from = context.droppableRects.get(current.id);
  const to = context.droppableRects.get(next.id);
  if (from && to) return { x: currentCoordinates.x + to.left - from.left, y: currentCoordinates.y };
};

export function ShootBoardDrag({
  onMove,
  children,
}: {
  onMove?: MoveShoot | undefined;
  children: ReactNode;
}) {
  const [active, setActive] = useState<SessionSummary | null>(null);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<{ error: boolean; text: string } | null>(null);
  const saving = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: columnKeyboardCoordinates }),
  );
  return (
    <DragState.Provider value={{ disabled: !onMove || pending }}>
      {feedback && (
        <p
          className={`shoot-board-feedback${feedback.error ? ' is-error' : ''}`}
          role={feedback.error ? 'alert' : 'status'}
        >
          {feedback.text}
        </p>
      )}
      {pending && (
        <p className="shoot-board-feedback" role="status">
          Saving status…
        </p>
      )}
      <DndContext
        sensors={sensors}
        accessibility={{
          screenReaderInstructions: {
            draggable:
              'Press Space to pick up. Use Left and Right arrows to choose a status column. Press Space to drop, or Escape to cancel.',
          },
        }}
        onDragStart={({ active }) => {
          setFeedback(null);
          setActive(active.data.current?.shoot ?? null);
        }}
        onDragCancel={() => setActive(null)}
        onDragEnd={({ active, over }) => {
          setActive(null);
          const shoot = active.data.current?.shoot as SessionSummary | undefined;
          const target = over?.data.current?.status as SessionStatus | undefined;
          if (!shoot || !target || target === shoot.status || !onMove || saving.current) return;
          if (target === 'Canceled') {
            setFeedback({
              error: true,
              text: 'Open the shoot and use Cancel shoot to confirm cancellation.',
            });
            return;
          }
          saving.current = true;
          setPending(true);
          setFeedback(null);
          void onMove(shoot, target)
            .then((moved) =>
              setFeedback(
                moved === false
                  ? null
                  : { error: false, text: `Moved to ${SESSION_STATUS_LABELS[target]}.` },
              ),
            )
            .catch((error: unknown) => {
              setFeedback({
                error: true,
                text:
                  error instanceof AdminApiError && error.status === 409
                    ? 'This shoot changed elsewhere. Check its latest details before trying again.'
                    : 'Could not confirm the status change. Check the card before trying again.',
              });
            })
            .finally(() => {
              saving.current = false;
              setPending(false);
            });
        }}
      >
        {children}
        {createPortal(
          <DragOverlay>
            {active && (
              <div className="shoot-board-drag-preview">
                {shootClientSummary(active).primaryClient.name}
              </div>
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </DragState.Provider>
  );
}
export function BoardDropColumn({
  status,
  children,
}: {
  status: SessionStatus;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `status:${status}`, data: { status } });
  return (
    <section
      ref={setNodeRef}
      className={`shoot-board__column shoot-board__column--${status}${isOver ? ' is-drop-target' : ''}`}
      aria-label={`${SESSION_STATUS_LABELS[status]} column`}
    >
      {children}
    </section>
  );
}
export function BoardDragCard({ shoot, children }: { shoot: SessionSummary; children: ReactNode }) {
  const { disabled } = useContext(DragState);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: shoot.id,
    data: { shoot },
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      className={`shoot-board__drag-card${isDragging ? ' is-dragging' : ''}`}
      onDragStart={(event) => event.preventDefault()}
    >
      <Link
        ref={setActivatorNodeRef}
        className="shoot-board__card"
        to={`/crm/shoots/${shoot.id}`}
        onPointerDown={(event) => listeners?.onPointerDown?.(event)}
        onKeyDown={(event) => {
          // Enter opens the link; Space starts keyboard dragging.
          if (event.code === 'Space') listeners?.onKeyDown?.(event);
        }}
        aria-describedby={disabled ? undefined : attributes['aria-describedby']}
        aria-roledescription={disabled ? undefined : 'draggable shoot'}
      >
        {children}
      </Link>
    </div>
  );
}
